import { posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'

/*
 * Building a page's world: a cell map clipped to the bounds and a few shapes every page draws
 * (a floor, boxes, balls, domes, height-field columns). Writes overwrite (last wins) unless a shape
 * says `keep`; the map keeps each key where it was first written, so a world's cell order (and so
 * its palette order) is the order its shapes first touched each cell.
 *
 *   const w = worldCells(BOUNDS)
 *   w.floor(0, FLOOR)
 *   w.box([30, 1, 6], [41, 3, 17], STEP)
 *   w.ellipsoid({ x: 24, y: 6, z: 24 }, { x: 5, y: 5, z: 5 }, CLAY)
 *   return createPageDocument(w.map, { ... })
 */

/** A cell value, or a function of the cell giving one (null or undefined: leave the cell alone). */
export type CellFill = VoxelData | ((x: number, y: number, z: number) => VoxelData | null | undefined)
type Corner = readonly [x: number, y: number, z: number]

export type WorldCells = {
  readonly bounds: Position
  /** The cells (key = posKeyXYZ), for createPageDocument or a flood fill. */
  readonly map: Map<number, VoxelData>
  /** One cell; outside the bounds it is dropped. */
  put(x: number, y: number, z: number, v: VoxelData): WorldCells
  get(x: number, y: number, z: number): VoxelData | undefined
  has(x: number, y: number, z: number): boolean
  /** Every cell from `from` to `to`, both corners included (x outermost, then y, then z). */
  box(from: Corner, to: Corner, v: CellFill): WorldCells
  /** One layer at height `y` over the whole footprint. */
  floor(y: number, v: VoxelData | ((x: number, z: number) => VoxelData)): WorldCells
  /**
   * The cells inside the ellipsoid ((p - center) / radius)^2 <= 1, p the cell's corner (or its
   * centre with `cellCenters`). `minY` cuts it flat from below; `keep` leaves cells already there.
   */
  ellipsoid(center: Position, radius: Position, v: CellFill, opts?: { cellCenters?: boolean; minY?: number; keep?: boolean }): WorldCells
  /**
   * A round dome standing on `y0` (default 1) at (cx, cz): radius `r` and `h` cells tall in the
   * middle (round(h * sqrt(1 - (d / r)^2)) cells per column, d from the cell's centre).
   */
  dome(cx: number, cz: number, r: number, h: number, v: CellFill, opts?: { y0?: number }): WorldCells
  /**
   * A height field: every column (x outermost, then z) filled from `from` (default 0) up to
   * `top(x, z)` inclusive (nothing when top < from), each cell `voxel(x, y, z, top)`.
   */
  columns(top: (x: number, z: number) => number, voxel: VoxelData | ((x: number, y: number, z: number, top: number) => VoxelData | null | undefined), opts?: { from?: number }): WorldCells
  /** The cells as entries, in first-written order. */
  cells(): Array<[number, VoxelData]>
}

export function worldCells(bounds: Position): WorldCells {
  const map = new Map<number, VoxelData>()
  const inside = (x: number, y: number, z: number) => x >= 0 && y >= 0 && z >= 0 && x < bounds.x && y < bounds.y && z < bounds.z
  const fill = (x: number, y: number, z: number, v: CellFill | null | undefined, keep = false) => {
    if (!inside(x, y, z)) return
    const key = posKeyXYZ(x, y, z)
    if (keep && map.has(key)) return
    const value = typeof v === 'function' ? v(x, y, z) : v
    if (value) map.set(key, value)
  }
  const w: WorldCells = {
    bounds,
    map,
    put(x, y, z, v) {
      fill(x, y, z, v)
      return w
    },
    get: (x, y, z) => map.get(posKeyXYZ(x, y, z)),
    has: (x, y, z) => map.has(posKeyXYZ(x, y, z)),
    box([x0, y0, z0], [x1, y1, z1], v) {
      for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) for (let z = z0; z <= z1; z++) fill(x, y, z, v)
      return w
    },
    floor(y, v) {
      for (let x = 0; x < bounds.x; x++) for (let z = 0; z < bounds.z; z++) fill(x, y, z, typeof v === 'function' ? v(x, z) : v)
      return w
    },
    ellipsoid(c, r, v, opts = {}) {
      const o = opts.cellCenters ? 0.5 : 0
      const y0 = Math.max(opts.minY ?? -Infinity, Math.floor(c.y - r.y))
      for (let x = Math.floor(c.x - r.x); x <= Math.ceil(c.x + r.x); x++) {
        for (let y = y0; y <= Math.ceil(c.y + r.y); y++) {
          for (let z = Math.floor(c.z - r.z); z <= Math.ceil(c.z + r.z); z++) {
            const d = ((x + o - c.x) / r.x) ** 2 + ((y + o - c.y) / r.y) ** 2 + ((z + o - c.z) / r.z) ** 2
            if (d <= 1) fill(x, y, z, v, opts.keep)
          }
        }
      }
      return w
    },
    dome(cx, cz, r, h, v, opts = {}) {
      const y0 = opts.y0 ?? 1
      for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
        for (let z = Math.floor(cz - r); z <= Math.ceil(cz + r); z++) {
          const d = Math.hypot(x + 0.5 - cx, z + 0.5 - cz)
          if (d > r) continue
          const top = Math.round(h * Math.sqrt(1 - (d / r) ** 2))
          for (let y = y0; y < y0 + top; y++) fill(x, y, z, v)
        }
      }
      return w
    },
    columns(top, voxel, opts = {}) {
      const from = opts.from ?? 0
      for (let x = 0; x < bounds.x; x++) {
        for (let z = 0; z < bounds.z; z++) {
          const t = top(x, z)
          for (let y = from; y <= t; y++) fill(x, y, z, typeof voxel === 'function' ? voxel(x, y, z, t) : voxel)
        }
      }
      return w
    },
    cells: () => Array.from(map),
  }
  return w
}

/** One shared voxel object per colour of a type (a world of many colours stays a few objects). */
export function voxelsByColor(type: string): (color: number) => VoxelData {
  const byColor = new Map<number, VoxelData>()
  return (color) => {
    let v = byColor.get(color)
    if (!v) byColor.set(color, (v = { type, color }))
    return v
  }
}
